// File: src/pages/ProductionStepsList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, CardBody, Button, Input, Spinner } from "reactstrap";
import swal from "sweetalert";
import {
  AllCategoryList,
  View_Catby_id,
  deleteProductionSteps,
} from "../import/ApiEndPoint/ApiCalling";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";

const injectOnce = () => {
  if (document.getElementById("psl-inline-css")) return;
  const s = document.createElement("style");
  s.id = "psl-inline-css";
  s.textContent = `
    .psl-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .psl-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }

    /* Table shell never scrolls page horizontally */
    .psl-table-wrap{ width:100%; overflow:hidden; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .psl-table{ width:100%; border-collapse:collapse; table-layout:fixed; }
    .psl-table thead th{ background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb; padding:8px 10px; text-align:left; white-space:nowrap; }
    .psl-table tbody td{ padding:10px; border-bottom:1px solid #f1f5f9; vertical-align:top; color:#0f172a; font-size:14px; }

    /* Columns */
    .psl-col-cat{ width:260px; }
    .psl-col-steps{ width:auto; }

    /* Horizontal steps scroller INSIDE the steps cell only */
    .psl-steps-scroll{
      display:flex;
      flex-wrap:nowrap;
      gap:10px;
      overflow-x:auto;
      overflow-y:hidden;
      padding-bottom:6px;
      scrollbar-width: thin;
    }
    .psl-steps-scroll::-webkit-scrollbar{ height:8px; }
    .psl-steps-scroll::-webkit-scrollbar-thumb{ background:#cbd5e1; border-radius:8px; }
    .psl-steps-scroll::-webkit-scrollbar-track{ background:transparent; }

    .psl-step{
      min-width:240px;
      max-width:240px;
      display:flex;
      gap:10px;
      align-items:flex-start;
      background:#f8fafc;
      border:1px solid #e5e7eb;
      border-radius:10px;
      padding:8px 10px;
      box-sizing:border-box;
    }
    .psl-step .bullet{ width:8px; height:8px; border-radius:999px; background:#0b5cf6; margin-top:6px; flex:0 0 8px; }
    .psl-step-body{ display:flex; flex-direction:column; gap:2px; min-width:0; }
    .psl-step-name{ font-weight:700; color:#0b5cf6; overflow-wrap:break-word; }
    .psl-step-note{ font-size:12px; color:#475569; overflow-wrap:break-word; }

    .psl-actions{ display:flex; gap:6px; margin-top:4px; }
    .psl-chip-del{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; background:#ec1809; color:#fff; font-weight:700; border:none; }
  `;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const withTimeout = (ms, promise) => {
  let timeoutId;
  const timeout = new Promise((_, rej) => {
    timeoutId = setTimeout(() => rej(new Error("Request timeout")), ms);
  });
  return Promise.race([promise, timeout]).finally(() =>
    clearTimeout(timeoutId)
  );
};

export default function ProductionStepsList() {
  useEffect(() => injectOnce(), []);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]); // [{catId, name, status, steps:[{_id, step_Name, note}]}]
  const [search, setSearch] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const res = await AllCategoryList(user?._id, user?.database);
      const cats = Array.isArray(res?.Category) ? res.Category : [];

      // Map basic rows
      const initialRows = cats.map((c) => ({
        catId: c?._id,
        name: c?.name || "-",
        status: c?.status || "-",
        steps: Array.isArray(c?.steps) ? c.steps : [],
      }));

      // Immediately show ONLY categories that already have steps
      const rowsWithStepsNow = initialRows.filter(
        (r) => Array.isArray(r.steps) && r.steps.length > 0
      );
      setRows(rowsWithStepsNow);
      setLoading(false);

      // For categories that didn't include steps, fetch details and
      // then again ONLY keep categories that have steps.
      const toFetchIds = initialRows
        .filter((r) => !Array.isArray(r.steps) || r.steps.length === 0)
        .map((r) => r.catId);

      if (toFetchIds.length) {
        const results = await Promise.allSettled(
          toFetchIds.map((id) => withTimeout(12000, View_Catby_id(id)))
        );

        const byId = {};
        results.forEach((res, idx) => {
          const id = toFetchIds[idx];
          if (res.status === "fulfilled") {
            const cat = res.value?.Category || {};
            byId[id] = Array.isArray(cat?.steps) ? cat.steps : [];
          }
        });

        if (Object.keys(byId).length) {
          const merged = initialRows.map((r) =>
            byId[r.catId] ? { ...r, steps: byId[r.catId] } : r
          );
          const onlyWithSteps = merged.filter(
            (r) => Array.isArray(r.steps) && r.steps.length > 0
          );
          setRows(onlyWithSteps);
        }
      }
    } catch (err) {
      console.error(err);
      setRows([]);
      setLoading(false);
      swal("Error", "Failed to load categories", "error");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      if (toStr(r?.name).toLowerCase().includes(term)) return true;
      if (toStr(r?.status).toLowerCase().includes(term)) return true;
      return (r?.steps || []).some(
        (s) =>
          toStr(s?.step_Name).toLowerCase().includes(term) ||
          toStr(s?.note).toLowerCase().includes(term)
      );
    });
  }, [rows, search]);

  const onDeleteStep = (catId, stepId) => {
    if (!catId || !stepId) return;
    swal("Warning", "Sure you want to delete this step?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (value) => {
      if (value === "delete") {
        try {
          const res = await deleteProductionSteps(catId, stepId);
          if (res?.status) {
            // Update local rows; if a category ends up with 0 steps, hide it
            setRows((prev) =>
              prev
                .map((row) =>
                  row.catId === catId
                    ? {
                        ...row,
                        steps: row.steps.filter((s) => s?._id !== stepId),
                      }
                    : row
                )
                .filter((row) => (row.steps || []).length > 0)
            );
            swal("Success!", "Step deleted successfully", "success");
          } else {
            swal("Error", "Error deleting step", "error");
          }
        } catch (err) {
          console.error(err);
          swal(
            "Error",
            "Something went wrong while deleting the step",
            "error"
          );
        }
      }
    });
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* Toolbar */}
      <Row className="psl-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="psl-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Production Steps by Category{" "}
                <span className="count-pill">{filtered.length}</span>
              </div>
              <div style={{ flex: "1 1 240px" }} />
              <div
                style={{
                  minWidth: 300,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search by category or step..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                  fontSize: 14,
                  textTransform: "uppercase",
                }}
                onClick={() => navigate("/app/production-steps/create")}
              >
                <FaPlus size={10} /> Production Step
              </Button>
              <Button
                outline
                color="secondary"
                style={{ height: 32 }}
                onClick={fetchAll}
              >
                Refresh
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col sm="12">
          <Card className="psl-card" style={{ padding: 8 }}>
            <div className="psl-table-wrap">
              <table className="psl-table">
                <colgroup>
                  <col className="psl-col-cat" />
                  <col className="psl-col-steps" />
                </colgroup>
                <thead>
                  <tr>
                    <th>CATEGORY NAME</th>
                    <th>STEPS (Horizontal Scroll)</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        style={{
                          padding: 18,
                          color: "#6b7280",
                          textAlign: "center",
                        }}
                      >
                        No categories with steps found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((r) => (
                      <tr key={r?.catId}>
                        <td>
                          <div
                            style={{
                              fontWeight: 800,
                              overflowWrap: "break-word",
                            }}
                          >
                            {r?.name || "-"}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "#64748b",
                              marginTop: 4,
                            }}
                          >
                            Status: <b>{r?.status || "-"}</b>
                          </div>
                        </td>
                        <td>
                          <div className="psl-steps-scroll">
                            {(r.steps || []).map((s) => (
                              <div key={s?._id} className="psl-step">
                                <div className="bullet" />
                                <div className="psl-step-body">
                                  <div className="psl-step-name">
                                    {s?.step_Name || "-"}
                                  </div>
                                  {s?.note ? (
                                    <div className="psl-step-note">
                                      {s.note}
                                    </div>
                                  ) : null}
                                  <div className="psl-actions">
                                    <button
                                      className="psl-chip-del"
                                      title="Delete Step"
                                      onClick={() =>
                                        onDeleteStep(r.catId, s?._id)
                                      }
                                    >
                                      ✕
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}
